import connect from 'components/lib/connect';
import CustomerViewerActivity from 'models/customer_viewer_activity';
import { CustomerViewersPill } from './customer_viewers_pill';

/**
 * Turns raw viewer records into the people to show.
 *
 * Records are per-tab, so one agent with two tabs on this customer would otherwise
 * appear twice. Our own sessions never reach the store — the gateway drops them on
 * the topic's agent segment, which is the half the broker binds to the publisher.
 */
export function mapStateToProps({ getProvider, isFeatureEnabled }) {
  // Read before the flag check, not after. connect registers a provider's binding
  // only for the names mapStateToProps touches on its mount pass, and isEnabled
  // reads false until all three feature sources have loaded — so on a cold load
  // landing straight on a customer, returning early here would leave the pill
  // observing nothing. It would then paint once, on the render the flag flip
  // triggers, and never again.
  const agentsProvider = getProvider('agents');
  const allViewers = getProvider('viewers').findAll();

  if (!isFeatureEnabled('agentsViewingCustomer')) {
    return { viewers: [] };
  }

  const byAgentId = new Map();
  allViewers.forEach(viewer => {
    if (!viewer.agentId) {
      return;
    }

    const existing = byAgentId.get(viewer.agentId);
    byAgentId.set(viewer.agentId, existing ? collapse(existing, toEntry(viewer)) : toEntry(viewer));
  });

  const viewers = Array.from(byAgentId.values())
    .map(entry => {
      const agent = agentsProvider.findBy({ id: entry.agentId });

      return {
        ...entry,
        // ProfileAvatar falls back to initials, and then to a placeholder, when
        // there's no image — so an agent who hasn't uploaded an avatar still reads
        // as a person rather than a gap.
        image: agent && agent.avatarUrl,
        // An agent we can't resolve still gets shown — that somebody is here is the
        // useful part, and a missing name is better than a vanishing colleague.
        name: (agent && (agent.name || agent.email)) || '',
      };
    })
    .sort((a, b) => arrivalTime(a.arrivedAt) - arrivalTime(b.arrivedAt));

  return { viewers };
}

function toEntry(viewer) {
  return {
    activity: viewer.activity,
    activityTarget: viewer.activityTarget,
    agentId: viewer.agentId,
    arrivedAt: viewer.arrivedAt,
  };
}

/**
 * One agent's sessions as a single entry: their earliest arrival, and the most
 * specific thing any of those sessions is doing.
 *
 * Taking the older record wholesale would report an agent idling in a stale tab as
 * "viewing" while they compose a reply in the tab in front of them.
 */
function collapse(existing, entry) {
  const composing = [existing, entry].find(candidate => candidate.activity === CustomerViewerActivity.COMPOSING);

  return {
    ...(composing || existing),
    arrivedAt: isEarlier(entry.arrivedAt, existing.arrivedAt) ? entry.arrivedAt : existing.arrivedAt,
  };
}

/**
 * Arrival as epoch milliseconds, for ordering only.
 *
 * Compared numerically rather than as strings: the value is minted by whichever
 * client published the record, so an offset form or a different fractional-second
 * precision would sort wrongly while still being the same instant. An unparseable
 * or absent value sorts last rather than poisoning the comparison with NaN.
 */
function arrivalTime(arrivedAt) {
  const time = arrivedAt ? new Date(arrivedAt).getTime() : NaN;

  return Number.isFinite(time) ? time : Infinity;
}

function isEarlier(candidate, current) {
  if (!candidate) {
    return false;
  }
  if (!current) {
    return true;
  }

  return arrivalTime(candidate) < arrivalTime(current);
}

export default connect(mapStateToProps)(CustomerViewersPill);
